import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Input } from 'reactstrap';
import { BiPlus, BiTrash, BiUser } from 'react-icons/bi';
import { toast } from 'react-toastify';
import { useJobAssignments, useAssignToJob, useUnassign } from '../../hooks/useJobAssignments';
import { fetchActiveMembers } from '../../api/membersApi';

// Display name fallbacks — prefer "First Last", then email, then "Member".
function displayName(row) {
    const full = [row.firstName, row.lastName].filter(Boolean).join(' ').trim();
    if (full) return full;
    return row.email || 'Member';
}

// "Member" → "Manager" for display only (DB role string stays "Member").
function displayRole(role) {
    if (role === 'Member') return 'Manager';
    return role || '';
}

// Manager+ surface on the job detail page. Lists assigned crew + offers
// "Add crew" to assign new ones. Workers never see this card (the parent
// page should not render it for them; defence-in-depth here would be the
// API returning 403).
export default function JobAssignmentsCard({ jobId }) {
    const assignments = useJobAssignments(jobId);
    const assignMut = useAssignToJob(jobId);
    const unassignMut = useUnassign(jobId);
    const [pickerOpen, setPickerOpen] = useState(false);

    const handleUnassign = (id) => {
        unassignMut.mutate(id, {
            onError: (err) => toast.error(err?.message ?? 'Failed to unassign.'),
        });
    };

    const handleAssign = (userId) => {
        assignMut.mutate(userId, {
            onSuccess: () => setPickerOpen(false),
            onError: (err) => toast.error(err?.message ?? 'Failed to assign.'),
        });
    };

    return (
        <>
            <div className="surface p-3 mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h2 className="fw-semibold fs-6 mb-0">Assigned crew</h2>
                    <Button color="primary" size="sm" outline
                            onClick={() => setPickerOpen(true)}
                            className="d-inline-flex align-items-center gap-1">
                        <BiPlus /> Add crew
                    </Button>
                </div>
                {assignments.isLoading ? (
                    <Spinner size="sm" />
                ) : assignments.error ? (
                    <div className="text-danger small">Failed to load crew.</div>
                ) : assignments.data?.length === 0 ? (
                    <div className="text-muted small">
                        No crew assigned yet. Add Workers (and any Managers who
                        also need mobile access) to give them visibility of this
                        job from the field.
                    </div>
                ) : (
                    assignments.data.map(a => (
                        <div key={a.id}
                             className="d-flex align-items-center gap-2 rounded px-2 py-2 mb-1"
                             style={{ background: 'var(--bs-tertiary-bg)' }}>
                            <BiUser className="text-muted flex-shrink-0" />
                            <div className="flex-grow-1">
                                <div className="fw-medium">{displayName(a)}</div>
                                <div className="text-muted small">
                                    {displayRole(a.role)}{a.email ? ` · ${a.email}` : ''}
                                </div>
                            </div>
                            <button className="btn btn-link p-0 text-danger flex-shrink-0"
                                    onClick={() => handleUnassign(a.id)}
                                    disabled={unassignMut.isPending}
                                    title="Remove from job">
                                <BiTrash size={16} />
                            </button>
                        </div>
                    ))
                )}
            </div>

            <AssignWorkerModal
                isOpen={pickerOpen}
                toggle={() => !assignMut.isPending && setPickerOpen(false)}
                excludeUserIds={(assignments.data ?? []).map(a => a.userId)}
                onPick={handleAssign}
                isPending={assignMut.isPending}
            />
        </>
    );
}

// Picker for "who do you want to assign?". Workers only — Managers/Admins
// already see every job by default, so listing them here is noise and
// risks accidentally "assigning" people who don't need a mobile-app entry
// for the job. Sorted alphabetically.
function AssignWorkerModal({ isOpen, toggle, excludeUserIds, onPick, isPending }) {
    const membersQuery = useQuery({
        queryKey: ['members', 'active'],
        queryFn: fetchActiveMembers,
        enabled: isOpen, // only fetch when the modal opens
    });

    const candidates = useMemo(() => {
        const all = membersQuery.data ?? [];
        const exclude = new Set(excludeUserIds);
        return all
            .filter(m => m.role === 'Worker' && !exclude.has(m.id))
            .sort((a, b) => displayName(a).localeCompare(displayName(b)));
    }, [membersQuery.data, excludeUserIds]);

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered>
            <ModalHeader toggle={toggle}>Assign crew to this job</ModalHeader>
            <ModalBody>
                {membersQuery.isLoading ? (
                    <div className="text-center py-3"><Spinner size="sm" /></div>
                ) : membersQuery.error ? (
                    <div className="text-danger small">Failed to load members.</div>
                ) : candidates.length === 0 ? (
                    <div className="text-muted small">
                        No Workers available. Either every Worker is already
                        assigned, or you haven't invited any yet — head to
                        Settings → Members to add one. Managers and Admins see
                        all jobs by default and don't need to be assigned.
                    </div>
                ) : (
                    candidates.map(m => (
                        <button key={m.id}
                                type="button"
                                className="d-flex align-items-center gap-2 w-100 text-start rounded px-2 py-2 mb-1 border"
                                style={{ background: 'var(--bs-tertiary-bg)' }}
                                disabled={isPending}
                                onClick={() => onPick(m.id)}>
                            <BiUser className="text-muted flex-shrink-0" />
                            <div className="flex-grow-1">
                                <div className="fw-medium">{displayName(m)}</div>
                                <div className="text-muted small">
                                    {displayRole(m.role)}{m.email ? ` · ${m.email}` : ''}
                                </div>
                            </div>
                        </button>
                    ))
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={toggle} disabled={isPending}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
}
